import React, { createContext, useContext, useState, useEffect } from 'react';
import authService, { RegistrationData } from "../services/authService";
import { getAuthToken, setAuthToken, removeAuthToken, getRefreshToken, setRefreshToken, removeRefreshToken, getUser, setUser, removeUser, clearAuthData, restoreSession } from '../utils/auth';

// Define the custom user type that matches the backend response
interface CustomUser {
  id: string;
  userId?: string;
  email?: string;
  phoneNumber?: string;
  firstName?: string;
  lastName?: string;
  grade?: string;
  school?: string;
  schoolId?: string;
  role?: string;
  onboardingCompleted?: boolean;
  [key: string]: any; // Allow additional properties
}

export type { CustomUser as User };

interface AuthContextType {
  user: CustomUser | null;
  token: string | null;
  loading: boolean;
  login: (emailOrPhone: string, password: string, captchaToken: string, role?: string, isPhoneNumber?: boolean) => Promise<void>;
  register: (data: RegistrationData) => Promise<void>;
  logout: () => void;
}

const AuthContext = createContext<AuthContextType>({
  user: null,
  token: null,
  loading: true,
  login: async () => {},
  register: async () => {},
  logout: () => {},
});

export const useAuth = () => useContext(AuthContext);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }: { children: React.ReactNode }) => {
  const [user, setUserState] = useState<CustomUser | null>(null);
  const [token, setTokenState] = useState<string | null>(null);
  const [refreshToken, setRefreshTokenState] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [isValidating, setIsValidating] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);

  useEffect(() => {
    const initializeAuth = async () => {
      try {
        console.log('Initializing authentication...');
        
        // Add a small delay to ensure localStorage is fully available
        await new Promise(resolve => setTimeout(resolve, 100));
        
        // Restore session from localStorage
        const restoredSession = await restoreSession();
        
        if (restoredSession) {
          const { user: restoredUser, token: restoredToken, refreshToken: restoredRefreshToken } = restoredSession;
          console.log('Session restored successfully:', {
            user: restoredUser.firstName || restoredUser.email,
            hasToken: !!restoredToken,
            hasRefreshToken: !!restoredRefreshToken
          });
          
          console.log('Restored user data structure:', {
            user: restoredUser,
            hasRole: !!restoredUser.role,
            role: restoredUser.role,
            userKeys: Object.keys(restoredUser),
            firstName: restoredUser.firstName,
            email: restoredUser.email
          });
          
          // Set state immediately to prevent race conditions
          setUserState(restoredUser);
          setTokenState(restoredToken);
          setRefreshTokenState(restoredRefreshToken);
          
          // Ensure localStorage is in sync with context state
          if (restoredToken) {
            setAuthToken(restoredToken);
          }
          if (restoredRefreshToken) {
            setRefreshToken(restoredRefreshToken);
          }
          setUser(restoredUser);
          
          console.log('Session restored from localStorage:', restoredUser.firstName || restoredUser.email);
          console.log('Auth state after restoration:', { 
            user: !!restoredUser, 
            token: !!restoredToken, 
            loading: false 
          });
          
          // CRITICAL: Mark as initialized BEFORE setting loading to false
          // This ensures RequireAuth knows the session restoration is complete
          setIsInitialized(true);
          
          // Set loading to false immediately after session restoration
          setLoading(false);
          
          // Validate token with backend in background (non-blocking)
          setTimeout(() => {
            if (restoredToken && restoredUser) {
              setIsValidating(true);
              validateTokenInBackground(restoredToken, restoredUser, restoredRefreshToken);
            }
          }, 100);
        } else {
          console.log('No saved session found in localStorage, initializing as new session.');
          setIsInitialized(true);
          setLoading(false);
        }
        
        console.log('Authentication initialization completed. Loading:', false, 'Initialized:', true);
      } catch (error) {
        console.error('Error during auth initialization:', error);
        // Don't clear session on initialization errors, just mark as initialized
        setIsInitialized(true);
        setLoading(false);
      }
    };

    initializeAuth();
  }, []);

  // Function to validate token with backend (non-blocking)
  const validateTokenInBackground = async (token: string, user: CustomUser | null, refreshToken?: string | null) => {
    try {
      const API_URL = process.env.REACT_APP_API_URL || "https://lavernaibackend.onrender.com/api";
      
      // Add timeout to prevent hanging requests
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 10000); // 10 second timeout
      
      const response = await fetch(`${API_URL}/auth/me`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        signal: controller.signal
      });
      
      clearTimeout(timeoutId);
      
      if (response.ok) {
        // Token is valid, optionally update user data
        try {
          const data = await response.json();
          if (data.data?.user || data.user) {
            const backendUser = data.data?.user || data.user;
            const updatedUser = { ...user, ...backendUser };
            setUserState(updatedUser);
            setUser(updatedUser);
          }
        } catch (parseError) {
          console.warn('Failed to parse token validation response:', parseError);
        }
        
        console.log('Token validation successful');
        setIsValidating(false);
        return;
      }

      // Only handle explicit authentication failures
      if (response.status === 401 || response.status === 403) {
        console.log('Token validation failed: Invalid or expired token');
        
        // Try to refresh the token if we have a refresh token
        if (refreshToken) {
          const refreshSuccess = await attemptTokenRefresh(refreshToken);
          if (refreshSuccess) {
            setIsValidating(false);
            return;
          }
        }
        
        // Only logout if we're certain the token is invalid
        // For now, let's be conservative and not logout on validation failures
        console.warn('Token validation failed but keeping session active - user may still be valid');
        setIsValidating(false);
      } else {
        // For other errors (500, network issues, timeouts), assume token is still valid
        console.warn(`Token validation failed with status ${response.status}, assuming token is valid`);
        setIsValidating(false);
      }
    } catch (error: any) {
      // Don't clear session on network errors, timeouts, or other non-auth failures
      if (error.name === 'AbortError') {
        console.debug('Token validation timeout (ignored)');
      } else {
        console.debug('Token validation network error (ignored):', error);
      }
      setIsValidating(false);
    }
  };

  // Function to attempt token refresh
  const attemptTokenRefresh = async (refreshTokenToUse: string): Promise<boolean> => {
    try {
      const newTokens = await authService.refreshToken(refreshTokenToUse);
      setTokenState(newTokens.token);
      setRefreshTokenState(newTokens.refreshToken);
      setAuthToken(newTokens.token);
      setRefreshToken(newTokens.refreshToken);
      console.log('Token refresh successful');
      return true;
    } catch (error) {
      console.error('Token refresh failed:', error);
      return false;
    }
  };

  // Heartbeat: keep user online (only when authenticated)
  useEffect(() => {
    if (!token || !user) return;
    
    let interval: NodeJS.Timeout;
    const deviceInfo = { deviceId: 'browser', deviceType: 'web', browser: navigator.userAgent, os: navigator.platform };
    
    const updateStatus = async () => {
      try {
        const API_URL = process.env.REACT_APP_API_URL || "https://lavernaibackend.onrender.com/api";
        await fetch(`${API_URL}/user-status/active`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ isActive: true, deviceInfo })
        });
        
        // Extend session timestamp on successful heartbeat
        const { extendSession } = await import('../utils/auth');
        extendSession();
      } catch (e) { 
        // Ignore heartbeat errors to prevent console spam
        console.debug('Heartbeat error (ignored):', e);
      }
    };
    
    updateStatus();
    interval = setInterval(updateStatus, 30000);
    return () => clearInterval(interval);
  }, [token, user]);

  // Extend session when user returns to tab
  useEffect(() => {
    if (!token || !user) return;
    
    let focusTimeout: NodeJS.Timeout;
    
    const handleFocus = async () => {
      // Debounce focus events to prevent multiple rapid calls
      clearTimeout(focusTimeout);
      focusTimeout = setTimeout(async () => {
        const { extendSession } = await import('../utils/auth');
        extendSession();
        console.log('Session extended on window focus');
      }, 100);
    };
    
    window.addEventListener('focus', handleFocus);
    return () => {
      window.removeEventListener('focus', handleFocus);
      clearTimeout(focusTimeout);
    };
  }, [token, user]);

  // Extend session on page visibility change (when user returns to tab)
  useEffect(() => {
    if (!token || !user) return;
    
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        const { extendSession } = require('../utils/auth');
        extendSession();
        console.log('Session extended on page visibility change');
      }
    };
    
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [token, user]);

  // Extend session on navigation (when user navigates between pages)
  useEffect(() => {
    if (!token || !user) return;
    
    const handleNavigation = () => {
      const { extendSession } = require('../utils/auth');
      extendSession();
      console.log('Session extended on navigation');
    };
    
    // Listen for navigation events
    window.addEventListener('popstate', handleNavigation);
    window.addEventListener('pushstate', handleNavigation);
    window.addEventListener('replacestate', handleNavigation);
    
    return () => {
      window.removeEventListener('popstate', handleNavigation);
      window.removeEventListener('pushstate', handleNavigation);
      window.removeEventListener('replacestate', handleNavigation);
    };
  }, [token, user]);

  const login = async (emailOrPhone: string, password: string, captchaToken: string, role?: string, isPhoneNumber: boolean = false) => {
    try {
      const response = await authService.login(emailOrPhone, password, captchaToken, role, isPhoneNumber);
      
      console.log('Login response received:', {
        user: response.user,
        hasRole: !!response.user.role,
        role: response.user.role,
        userKeys: Object.keys(response.user)
      });
      
      setTokenState(response.token);
      setUserState(response.user as CustomUser);
      setAuthToken(response.token);
      setUser(response.user);
      
      // Store refresh token if provided
      if (response.refreshToken) {
        setRefreshTokenState(response.refreshToken);
        setRefreshToken(response.refreshToken);
      }
      
      console.log('User data stored in context and localStorage:', {
        contextUser: response.user,
        contextUserRole: response.user.role,
        localStorageUser: getUser()
      });
    } catch (error) {
      console.error("Login error:", error);
      throw error;
    }
  };

  const register = async (data: RegistrationData) => {
    try {
      const response = await authService.register(data);
      // Don't automatically authenticate the user after registration
      // They need to go through the login flow with captcha verification
      // setTokenState(response.token);
      // setUserState(response.user as CustomUser);
      // setAuthToken(response.token);
      // setUser(response.user);
      
      // Just return the response for the component to handle
      return response;
    } catch (error) {
      console.error("Registration error:", error);
      throw error;
    }
  };

  const logout = () => {
    setUserState(null);
    setTokenState(null);
    setRefreshTokenState(null);
    clearAuthData();
  };

  // Only show loading if we're still initializing
  // Don't show loading during background validation as it blocks the UI unnecessarily
  // Also don't show loading if we already have a user (prevents unnecessary loading on navigation)
  // And don't show loading if we have localStorage data but haven't finished parsing it
  // Also consider if this is a page refresh (which might need more time)
  const hasLocalStorageData = localStorage.getItem('authToken') && localStorage.getItem('authUser');
  const isPageRefresh = (performance as any).navigation?.type === 1 || 
                       (performance.getEntriesByType('navigation')[0] as any)?.type === 'reload';
  
  // Improved loading logic: only show loading if we're truly initializing and have no session data
  // This prevents loading on page refresh when we have localStorage data
  // Also ensure we wait for session restoration to complete
  // CRITICAL: Don't show loading if we have a user (prevents unnecessary loading states)
  // CRITICAL: Don't show loading if we're initialized and have localStorage data
  const isLoading = loading && (!user && !hasLocalStorageData) || (!isInitialized && hasLocalStorageData);
  
  // Debug logging for authentication state
  if (process.env.NODE_ENV === 'development') {
    console.log('Auth state:', { 
      user: !!user, 
      token: !!token, 
      loading, 
      isInitialized, 
      isValidating, 
      isLoading,
      hasLocalStorageData,
      isPageRefresh
    });
  }

  // Debug function to help troubleshoot authentication issues
  useEffect(() => {
    if (process.env.NODE_ENV === 'development') {
      (window as any).debugAuthState = () => {
        console.log('=== AUTH STATE DEBUG ===');
        console.log('Context State:', {
          user: !!user,
          userData: user,
          token: !!token,
          loading,
          isInitialized,
          isValidating,
          isLoading
        });
        console.log('LocalStorage:', {
          hasToken: !!localStorage.getItem('authToken'),
          hasUser: !!localStorage.getItem('authUser'),
          hasRefreshToken: !!localStorage.getItem('refreshToken'),
          tokenLength: localStorage.getItem('authToken')?.length || 0,
          userLength: localStorage.getItem('authUser')?.length || 0
        });
        console.log('Session Data:', {
          hasLocalStorageData,
          isPageRefresh
        });
        console.log('=======================');
      };
      
      console.log('Debug function available: window.debugAuthState()');
    }
  }, [user, token, loading, isInitialized, isValidating, isLoading, hasLocalStorageData, isPageRefresh]);

  return (
    <AuthContext.Provider value={{ user, token, loading: isLoading, login, register, logout }}>
      {children}
    </AuthContext.Provider>
  );
};
